import type { CSSProperties, ReactNode } from 'react';
import './tabela.css';

export type ColunaTabela = {
  chave: string;
  rotulo: string;
  /** Alinha à direita com dígito tabular. */
  numero?: boolean;
  /** `title` do cabeçalho: o que a coluna conta, quando o rótulo é curto. */
  titulo?: string;
  /** Rótulo só para leitor de tela — a coluna de ação, cujo cabeçalho seria ruído. */
  rotuloOculto?: boolean;
  /**
   * Classe do `<col>` — é onde a LARGURA da coluna mora, no CSS da tela.
   *
   * Quais colunas existem e quanto cada uma ocupa é decisão de quem usa a
   * tabela, não do componente: a mesma peça serve a uma tabela de seis colunas
   * e a uma de duas.
   */
  classeDaColuna?: string;
  /** O cabeçalho vira botão e pede a ordenação por esta coluna. */
  ordenavel?: boolean;
};

export type TabelaProps = {
  colunas: ColunaTabela[];
  /** Uma entrada por linha. A primeira coluna vira `<th>` da linha. */
  linhas: Record<string, ReactNode>[];
  /**
   * Linhas agrupadas, com uma fileira de título por grupo — um `tbody` cada.
   *
   * É a estrutura que a linha do tempo tem: eventos agrupados por dia,
   * comparáveis coluna a coluna. Um `tbody` por grupo é o que o HTML oferece
   * para isso, e é o que faz o leitor de tela anunciar a mudança de seção.
   *
   * Quando há grupos, `linhas` é ignorado: são duas formas da mesma tabela, e
   * misturá-las produziria uma ordem que ninguém decidiu.
   */
  grupos?: { titulo: string; linhas: Record<string, ReactNode>[] }[];
  /** Chave estável de cada linha. */
  chaveDe: (linha: Record<string, ReactNode>, indice: number) => string;
  densa?: boolean;
  /** Nota abaixo da tabela: de onde vêm os números, o que não entra na conta. */
  legenda?: string;
  /** O que a tabela mostra, para o leitor de tela. */
  rotulo?: string;
  /** Classe extra no invólucro que rola. */
  classe?: string;
  /** Classe extra na tabela: largura mínima, `table-layout`. */
  classeDaTabela?: string;
  /**
   * Por qual coluna a lista está ordenada, e para onde.
   *
   * Vem de fora porque quem ordena é o servidor: a tabela não tem as outras
   * páginas, e ordenar o que está na tela produziria uma ordem que muda a cada
   * rolagem.
   */
  ordem?: { chave: string; direcao: 'asc' | 'desc' };
  aoOrdenar?: (chave: string) => void;
  /**
   * Abre o item da linha com um clique em qualquer lugar dela.
   *
   * É atalho de MOUSE: o clique num botão, link ou menu dentro da linha
   * continua sendo daquele controle. Quem usa teclado precisa de um controle
   * de verdade na linha que faça o mesmo (o nome, por exemplo): uma `tr`
   * focável seria uma linha que o leitor de tela anuncia como botão sem nome.
   */
  aoAbrirLinha?: (linha: Record<string, ReactNode>) => void;
  /**
   * Linhas selecionáveis: uma coluna de caixas antes da primeira, e no
   * cabeçalho a que marca ou desmarca todas as visíveis.
   *
   * A seleção mora em quem usa a tabela (é ele quem age sobre ela); a tabela
   * só desenha as caixas e avisa. A primeira coluna continua sendo o cabeçalho
   * da linha: a caixa é um controle, e o leitor de tela anuncia a linha pelo
   * nome, não por "caixa de seleção".
   */
  selecao?: {
    /** As chaves (`chaveDe`) das linhas marcadas. */
    readonly marcadas: ReadonlySet<string>;
    readonly aoAlternar: (chave: string) => void;
    /** Marca todas as visíveis, ou desmarca todas quando já estão. */
    readonly aoAlternarTodas: () => void;
    /** O nome da caixa de cada linha: "Selecionar Marcos Silva". */
    readonly rotuloDe: (linha: Record<string, ReactNode>) => string;
    /** O nome da caixa do cabeçalho: "Selecionar todos os clientes visíveis". */
    readonly rotuloDeTodas: string;
  };
  /**
   * As linhas entram uma a uma ao aparecer (#765, a lista de organizações).
   *
   * Opcional porque é decisão da tela: uma tabela de relatório que se refaz a
   * cada filtro não pode ficar piscando. A posição de cada linha vai no
   * `style` (`--i`), e o atraso para na 10ª.
   */
  entrada?: boolean;
};

/** O clique que nasce num controle é dele, e não da linha. */
const CONTROLE = 'a, button, input, select, textarea, label, [role="menu"], [role="menuitem"]';

/**
 * A rolagem é do invólucro, e o componente sempre traz o invólucro.
 *
 * Deixar isso para a tela foi o que produziu uma tabela rolando no corpo da
 * página na administração.
 */
export function Tabela({
  colunas,
  linhas,
  grupos,
  chaveDe,
  densa = false,
  legenda,
  rotulo,
  classe,
  classeDaTabela,
  ordem,
  aoOrdenar,
  aoAbrirLinha,
  selecao,
  entrada = false,
}: TabelaProps) {
  const [primeira, ...demais] = colunas;
  const temLargura = colunas.some((coluna) => coluna.classeDaColuna);
  const chaves = (grupos ? grupos.flatMap((g) => g.linhas) : linhas).map((linha, indice) =>
    chaveDe(linha, indice),
  );
  const todasMarcadas =
    selecao !== undefined && chaves.length > 0 && chaves.every((c) => selecao.marcadas.has(c));
  const algumaMarcada = selecao !== undefined && chaves.some((c) => selecao.marcadas.has(c));

  return (
    <div className={classe ? `tabela-rolagem ${classe}` : 'tabela-rolagem'}>
      <table
        className={[
          densa ? 'tabela tabela--densa' : 'tabela',
          entrada ? 'tabela--entrada' : null,
          classeDaTabela,
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={rotulo}
      >
        {legenda ? <caption className="tabela__legenda">{legenda}</caption> : null}
        {/* O `colgroup` só aparece quando alguém pediu largura: uma grade vazia
            no HTML seria ruído para quem lê o DOM procurando o defeito. */}
        {temLargura ? (
          <colgroup>
            {selecao ? <col className="tabela__col-selecao" /> : null}
            {colunas.map((coluna) => (
              <col key={coluna.chave} className={coluna.classeDaColuna} />
            ))}
          </colgroup>
        ) : null}
        <thead>
          <tr>
            {selecao ? (
              <th scope="col" className="tabela__selecao">
                <input
                  type="checkbox"
                  className="tabela__caixa"
                  aria-label={selecao.rotuloDeTodas}
                  checked={todasMarcadas}
                  // Parte marcada: o traço do "algumas", e não a caixa vazia.
                  ref={(caixa) => {
                    if (caixa) caixa.indeterminate = algumaMarcada && !todasMarcadas;
                  }}
                  onChange={selecao.aoAlternarTodas}
                />
              </th>
            ) : null}
            {colunas.map((coluna) => {
              const ordenada = ordem?.chave === coluna.chave;
              const nome = coluna.rotuloOculto ? (
                <span className="visualmente-oculto">{coluna.rotulo}</span>
              ) : (
                coluna.rotulo
              );

              return (
                <th
                  key={coluna.chave}
                  scope="col"
                  className={coluna.numero ? 'tabela__num' : undefined}
                  title={coluna.titulo}
                  /*
                   * `aria-sort` vai na CÉLULA, e não no botão.
                   *
                   * É o cabeçalho que está ordenado, não o controle. Sem ele,
                   * quem usa leitor de tela não sabe por qual coluna a lista
                   * está ordenada — e a ordem é metade da informação numa fila
                   * de trabalho.
                   */
                  aria-sort={
                    ordenada ? (ordem.direcao === 'asc' ? 'ascending' : 'descending') : undefined
                  }
                >
                  {coluna.ordenavel && aoOrdenar ? (
                    <button
                      type="button"
                      className="tabela__ordenar"
                      onClick={() => aoOrdenar(coluna.chave)}
                    >
                      {nome}
                      {/* A seta é decoração: o estado quem anuncia é o
                          `aria-sort` acima, e lê-la duas vezes seria ruído. */}
                      <span className="tabela__seta" aria-hidden="true">
                        {ordenada && ordem.direcao === 'asc' ? '↑' : '↓'}
                      </span>
                    </button>
                  ) : (
                    nome
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        {grupos
          ? grupos.map((grupo) => (
              <tbody key={grupo.titulo}>
                {/* A fileira de título do grupo. `colspan` porque ela nomeia a
                    seção inteira, e não uma coluna. */}
                <tr className="tabela__grupo">
                  <th scope="colgroup" colSpan={colunas.length + (selecao ? 1 : 0)}>
                    {grupo.titulo}
                  </th>
                </tr>
                {grupo.linhas.map((linha, indice) => (
                  <Fileira
                    key={chaveDe(linha, indice)}
                    chave={chaveDe(linha, indice)}
                    linha={linha}
                    primeira={primeira}
                    demais={demais}
                    aoAbrir={aoAbrirLinha}
                    selecao={selecao}
                    posicao={entrada ? indice : undefined}
                  />
                ))}
              </tbody>
            ))
          : null}
        {grupos ? null : (
          <tbody>
            {linhas.map((linha, indice) => (
              <Fileira
                key={chaveDe(linha, indice)}
                chave={chaveDe(linha, indice)}
                linha={linha}
                primeira={primeira}
                demais={demais}
                aoAbrir={aoAbrirLinha}
                selecao={selecao}
                posicao={entrada ? indice : undefined}
              />
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
}

/** Uma fileira, igual nas duas formas — agrupada e solta. */
function Fileira({
  chave,
  linha,
  primeira,
  demais,
  aoAbrir,
  selecao,
  posicao,
}: {
  chave: string;
  linha: Record<string, ReactNode>;
  primeira: ColunaTabela | undefined;
  demais: ColunaTabela[];
  aoAbrir?: (linha: Record<string, ReactNode>) => void;
  selecao?: TabelaProps['selecao'];
  /** Com a entrada ligada: a posição da linha, que atrasa a entrada dela. */
  posicao?: number;
}) {
  const marcada = selecao?.marcadas.has(chave) ?? false;
  return (
    <tr
      className={
        [aoAbrir ? 'tabela__fileira--clicavel' : null, marcada ? 'tabela__fileira--marcada' : null]
          .filter(Boolean)
          .join(' ') || undefined
      }
      style={posicao === undefined ? undefined : ({ '--i': posicao } as CSSProperties)}
      onClick={
        aoAbrir
          ? (evento) => {
              if ((evento.target as Element).closest(CONTROLE)) return;
              // Quem arrastou para copiar o telefone não queria abrir a ficha.
              if ((globalThis.getSelection?.()?.toString() ?? '') !== '') return;
              aoAbrir(linha);
            }
          : undefined
      }
    >
      {selecao ? (
        <td className="tabela__selecao">
          <input
            type="checkbox"
            className="tabela__caixa"
            aria-label={selecao.rotuloDe(linha)}
            checked={marcada}
            onChange={() => selecao.aoAlternar(chave)}
          />
        </td>
      ) : null}
      {primeira ? <th scope="row">{linha[primeira.chave]}</th> : null}
      {demais.map((coluna) => (
        <td key={coluna.chave} className={coluna.numero ? 'tabela__num' : undefined}>
          {linha[coluna.chave]}
        </td>
      ))}
    </tr>
  );
}
